// src/components/GuestTable.js
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditTableDialog from "./EditTableDialog";
import { updateArrangement } from "../Service";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AddIcon from "@mui/icons-material/Add";

const GuestTable = ({
  invitees = [],
  FRONT_URL,
  handleEditClick,
  arr_name,
  arr_side,
  arr_id,
  load,
  errorMessage,
  setErrorMessage,
  openAlert,
  setOpenAlert,
  successMessage,
  setSuccessMessage,
  openSuccess,
  setOpenSuccess,
  loading,
  setLoading,
  handleDeleteTable,
  setEachTableGuestAddDialogOpen,
  setEachTableAddGuestTableName,
  setEachTableAddGuestTableId,
}) => {
  // ダイアログの状態を管理
  const [editTableDialogOpen, setEditTableDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState({
    arrangement_name: arr_name,
    side: arr_side,
    id: arr_id,
  });
  const [isCopied, setIsCopied] = useState(false);
  const theme = useTheme();

  const getSideText = (side) => {
    let label, color;

    switch (side) {
      case "groom":
        label = "新郎";
        color = theme.palette.groom.main;
        break;
      case "bride":
        label = "新婦";
        color = theme.palette.bride.main;
        break;
      case "N":
        label = "未定義";
        color = theme.palette.undefined.main;
        break;
      default:
        label = "";
        color = "default";
        break;
    }

    return (
      <Chip
        label={label}
        color="groom"
        size="small"
        sx={{
          backgroundColor: color, // バッジの背景色
          color: "#ffffff",
        }}
      />
    );
  };

  const getGenderText = (gender) => {
    switch (gender) {
      case "M":
        return "男";
      case "F":
        return "女";
      case "N":
        return "未定義";
      default:
        return ""; // 万が一のためのデフォルト
    }
  };

  const getTravelCostText = (travelCost) => {
    console.log(travelCost);
    switch (travelCost) {
      case "no":
        return "負担なし";
      case "yes":
        return "負担あり";
      default:
        return "負担なし";
    }
  };

  const getWillChip = (will) => {
    let label, color;

    switch (will) {
      case "yet":
        label = "未確定";
        color = "default"; // デフォルトの色
        break;
      case "yes":
        label = "参加";
        color = "primary"; // 成功色
        break;
      case "no":
        label = "不参加";
        color = "secondary"; // エラー色
        break;
      default:
        label = "不明"; // 万が一のためのデフォルト
        color = "default";
    }

    return <Chip label={label} color={color} sx={{}} />;
  };

  const getInvitedByText = (
    invited_by,
    first_name,
    last_name,
    first_furigana,
    last_furigana
  ) => {
    let name;
    if (invited_by === "host") {
      name = "ホスト";
    } else {
      name = last_name + "　" + first_name;
    }

    return (
      <>
        {/* <Typography variant="body2" color="textSecondary">{furigana}</Typography> */}
        {name}
      </>
    );
  };

  const handleEditTable = async (updatedTale) => {
    setLoading(true);
    try {
      await updateArrangement(arr_id, updatedTale);
      await load();
      setLoading(false);
      setEditTableDialogOpen(false);
      setSuccessMessage("テーブルの編集成功");
      setOpenSuccess(true);
      setTimeout(() => {
        setOpenSuccess(false);
      }, 3000);
      setErrorMessage("");
    } catch (error) {
      setLoading(false);
      console.error("Failed to update arrangement:", error);
      setErrorMessage(error.response?.data?.message || "Error updating table.");
      setOpenAlert(true);
    }
  };

  const handleCopy = () => {
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false); // 3秒後にメッセージを非表示
    }, 3000); // 3000ミリ秒（3秒）後にメッセージを消す
  };

  const handleEachTableAddGuest = () => {
    setEachTableAddGuestTableName(arr_name);
    setEachTableAddGuestTableId(arr_id);
    setEachTableGuestAddDialogOpen(true);
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }}>
          {getSideText(arr_side)}

          <Typography variant="h6" style={{ padding: "0 0 0 6px" }}>
            {arr_name}
          </Typography>

          {arr_id && ( // arr_id が空でない場合にボタンを表示
            <IconButton
              variant="text"
              color="primary"
              onClick={() => {
                setSelectedTable({
                  arrangement_name: arr_name,
                  side: arr_side,
                  id: arr_id,
                });
                setEditTableDialogOpen(true);
              }}
            >
              <EditNoteIcon />
            </IconButton>
          )}
        </Stack>

        {arr_id && (
          <IconButton
            variant="contained"
            sx={{
              width: 32, // 幅
              height: 32, // 高さ
              backgroundColor: theme.palette.card.default,
              borderRadius: 1,
              padding: 1,
              boxShadow: theme.shadows[1],
              "&:hover": {
                boxShadow: theme.shadows[3],
              },
            }}
            onClick={() => {
              handleEachTableAddGuest();
            }}
          >
            <AddIcon color="primary" />
          </IconButton>
        )}
      </Stack>

      <TableContainer component={Paper} className="table-container">
        <Table aria-label="guest table">
          <TableHead>
            <TableRow>
              <TableCell className="sticky-col">呼び名</TableCell>
              <TableCell>招待状</TableCell>
              <TableCell>苗字</TableCell>
              <TableCell>名前</TableCell>
              <TableCell>性別</TableCell>
              <TableCell>アレルギー</TableCell>
              <TableCell>招待者</TableCell>
              <TableCell>参加予定</TableCell>
              <TableCell>交通費</TableCell>
              <TableCell>SNS許可</TableCell>
              <TableCell>送金済み</TableCell>
              <TableCell>編集</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitees.map((invitee) => (
              <TableRow key={invitee.id}>
                <TableCell className="sticky-col">
                  {invitee.nick_name}
                </TableCell>
                <TableCell>
                  <CopyToClipboard
                    text={`${FRONT_URL}/guest/invitation/${invitee.id}`}
                    onCopy={handleCopy}
                  >
                    <IconButton color="primary" title="コピー">
                      <ContentCopyIcon />
                    </IconButton>
                  </CopyToClipboard>
                  {/* <IconButton
                                    color="primary"
                                    title="リンクを新しいタブで開く"
                                    onClick={() => window.open(`${FRONT_URL}/guest/invitation/${invitee.id}`, '_blank', 'noopener,noreferrer')}
                                >
                                    <OpenInNewIcon />
                                </IconButton> */}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {invitee.last_furigana}
                  </Typography>
                  {invitee.last_name}
                </TableCell>
                <TableCell>
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      {invitee.first_furigana}
                    </Typography>
                    {invitee.first_name}
                  </div>
                </TableCell>
                <TableCell>{getGenderText(invitee.gender)}</TableCell>
                <TableCell style={{ maxWidth: "100px", overflow: "auto" }}>
                  {invitee.allergy}
                </TableCell>
                <TableCell>
                  {getInvitedByText(
                    invitee.invited_by,
                    invitee.invited_by_first_name,
                    invitee.invited_by_last_name,
                    invitee.invited_by_first_furigana,
                    invitee.invited_by_last_furigana
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {getWillChip(invitee.will)} {/* 参加予定のChipを表示 */}
                  </Stack>
                </TableCell>
                <TableCell>{getTravelCostText(invitee.travel_cost)}</TableCell>
                <TableCell>{invitee.sns_permission}</TableCell>
                <TableCell>
                  <Tooltip title="一番右の、「編集」から編集してください。">
                    {invitee.is_paid ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleEditClick(invitee)}
                    color="primary"
                    title="編集"
                  >
                    <ManageAccountsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* コピーされたら表示するSnackbar */}
      <Snackbar
        open={isCopied}
        autoHideDuration={2000} // 3秒後に自動的に消える
        onClose={() => setIsCopied(false)}
        message="コピーされました"
      >
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
          コピーされました
        </Alert>
      </Snackbar>

      {/* 編集ダイアログ */}
      <EditTableDialog
        open={editTableDialogOpen}
        onClose={() => setEditTableDialogOpen(false)}
        table={selectedTable}
        onEdit={handleEditTable}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        load={load}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        openSuccess={openSuccess}
        setOpenSuccess={setOpenSuccess}
        loading={loading}
        setLoading={setLoading}
        handleDeleteTable={handleDeleteTable}
      />
    </>
  );
};

export default GuestTable;
